import { SERVER_HOST } from "./constants";

// Sales Report
const getSalesReport = async (start, end) => {
    const response = await fetch(
        SERVER_HOST +
            "?action=get_sales_report" +
            "&start_date=" +
            start +
            "&end_date=" +
            end
    );
    const data = await response.json();

    if (data.status !== "success") {
        throw new Error("Failed to fetch data!");
    }

    const transformData = (item) => {
        const date =
            item.date.substring(8, 10) + "." + item.date.substring(5, 7);
        const price = parseFloat(item.total_price);
        const cost = parseFloat(item.total_cost);
        const income = Math.round((price - cost) * 100) / 100;

        return {
            label: date,
            revenue: [price],
            cost: [cost],
            income: [income],
            rci: [price, cost, income],
        };
    };

    const result = data.data.reduce(
        (acc, item) => {
            const { label, revenue, cost, income, rci } = transformData(item);
            acc.revenue.push({ label, values: revenue });
            acc.cost.push({ label, values: cost });
            acc.income.push({ label, values: income });
            acc.rci.push({ label, values: rci });
            return acc;
        },
        { revenue: [], cost: [], income: [], rci: [] }
    );

    return result;
};

// Efficiency Statistic
const getOverallIncome = async (start, end) => {
    const fetchIncome = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_overall_income" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return parseFloat(data.data.total_income).toFixed(2);
    };

    const [result] = await Promise.all([fetchIncome(start, end)]);

    return isNaN(result) ? 0 : result;
};

const getAverageCheck = async (start, end) => {
    const fetchAverageCheck = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_average_check" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return parseFloat(data.data.average_check).toFixed(2);
    };

    const [result] = await Promise.all([fetchAverageCheck(start, end)]);

    return isNaN(result) ? 0 : result;
};

const getCheckCount = async (start, end) => {
    const fetchCheckCount = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_check_count" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return parseInt(data.data.check_count);
    };

    const [result] = await Promise.all([fetchCheckCount(start, end)]);

    return isNaN(result) ? 0 : result;
};

const getStaffEfficiency = async (start, end) => {
    const fetchStaffEfficiency = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_staff_efficiency" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchStaffEfficiency(start, end)]);

    return result;
};

const getDepartmentEfficiency = async (start, end) => {
    const fetchDepartmentEfficiency = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_department_efficiency" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchDepartmentEfficiency(start, end)]);

    return result;
};

// Payment Types
const getPaymentTypes = async (startDate, endDate) => {
    const typeToLabel = {
        0: "Готівка",
        1: "Карта",
        2: "Онлайн",
        4: "Сертифікат",
        6: "В кредит",
    };

    function mergeType2and3(arr) {
        let type2 = arr.find((item) => item.type === 2);
        let type3Index = arr.findIndex((item) => item.type === 3);

        if (type2 && type3Index !== -1) {
            type2.total_amount =
                parseFloat(type2.total_amount) +
                parseFloat(arr[type3Index].total_amount);
            arr.splice(type3Index, 1);
        }

        return arr;
    }

    const fetchPaymentTypes = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_payment_types" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return mergeType2and3(data.data).map((item) => ({
            label: typeToLabel[item.type] ? typeToLabel[item.type] : "(пусто)",
            values: [parseFloat(item.total_amount)],
        }));
    };

    const [lastMonthResult] = await Promise.all([
        fetchPaymentTypes(startDate, endDate),
    ]);

    return {
        lastMonth: lastMonthResult,
    };
};

// Peek Hours
const getPeakHours = async (startDate, endDate) => {
    const today = new Date();
    const timezoneOffset = -(today.getTimezoneOffset() / 60);
    const fetchPeakHours = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_peak_hours" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data.map((item) => ({
            label:
                item.hour + timezoneOffset < 10
                    ? `0${item.hour + timezoneOffset}:00`
                    : `${item.hour + timezoneOffset}:00`,
            values: [parseInt(item.client_amount)],
        }));
    };

    const [lastMonthResult] = await Promise.all([
        fetchPeakHours(startDate, endDate),
    ]);

    return {
        lastMonth: lastMonthResult,
    };
};

// Popular Menu
const getPopularMenu = async (startDate, endDate) => {
    const fetchPopularMenu = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_popular_menu" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data.map((item) => ({
            id: item.id,
            parent_id: item.pid,
            marked: item.marked,
            label: item.name,
            values: [parseFloat(item.amount)],
        }));
    };

    const [lastMonthResult] = await Promise.all([
        fetchPopularMenu(startDate, endDate),
    ]);

    return {
        lastMonth: lastMonthResult,
    };
};

// Loss Sold Goods
const getLossSoldGoods = async (startDate, endDate) => {
    const fetchLossSoldGoods = async (startDate, endDate) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_loss_sold_goods" +
                "&start_date=" +
                startDate.toISOString() +
                "&end_date=" +
                endDate.toISOString()
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [lastMonthResult] = await Promise.all([
        fetchLossSoldGoods(startDate, endDate),
    ]);

    return { lastMonth: lastMonthResult };
};

// Suppliers Debt
const getSuppliersDebt = async (endDate) => {
    const fetchSuppliersDebt = async (endDate) => {
        const response = await fetch(
            SERVER_HOST + "?action=get_suppliers_debt&end_date=" + endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [overallResult] = await Promise.all([fetchSuppliersDebt(endDate)]);

    return {
        list: overallResult,
        sum: overallResult
            .reduce((total, item) => total + item.debt, 0)
            .toFixed(2),
    };
};

const getDebtReport = async (start, end, counterpartyId) => {
    const fetchDebtReport = async (startDate, endDate, counterpartyId) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_debt_report" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate +
                "&counterparty_id=" +
                counterpartyId
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([
        fetchDebtReport(start, end, counterpartyId),
    ]);

    return result;
};

// Cash Balances
const getCashBalances = async (endDate) => {
    const fetchCashBalances = async (endDate) => {
        const response = await fetch(
            SERVER_HOST + "?action=get_cash_balances&end_date=" + endDate
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [overallResult] = await Promise.all([fetchCashBalances(endDate)]);

    return {
        list: overallResult,
        sum: overallResult
            .reduce((total, item) => total + item.balance, 0)
            .toFixed(2),
    };
};

const getCashflowReport = async (start, end, cashAccId = null) => {
    const fetchCashflowReport = async (startDate, endDate, cashAccId) => {
        const response = await fetch(
            SERVER_HOST +
                "?action=get_cashflow_report" +
                "&start_date=" +
                startDate +
                "&end_date=" +
                endDate +
                (cashAccId === null ? "" : "&cash_acc_id=" + cashAccId)
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([
        fetchCashflowReport(start, end, cashAccId),
    ]);

    return result;
};

// Statement
const getCashAccounts = async () => {
    const fetchCashAccounts = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_cash_accounts");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchCashAccounts()]);

    return result;
};

const getArticles = async () => {
    const fetchArticles = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_articles");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchArticles()]);

    return result;
};

const getDepartments = async () => {
    const fetchDepartments = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_departments");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchDepartments()]);

    return result;
};

const getCounterparties = async () => {
    const fetchCounterparties = async () => {
        const response = await fetch(
            SERVER_HOST + "?action=get_counterparties"
        );

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchCounterparties()]);

    return result;
};

const getDiscounts = async () => {
    const fetchDiscounts = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_discounts");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchDiscounts()]);

    return result;
};

// Stop-list
const getStopList = async () => {
    const response = await fetch(SERVER_HOST + "?action=get_stop_list");
    const data = await response.json();

    if (data.status !== "success") {
        throw new Error("Failed to fetch stop list data!");
    }

    return data.data;
};

const getFolders = async () => {
    const fetchFolders = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_folders");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchFolders()]);

    return result;
};

const getGoods = async () => {
    const fetchGoods = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_goods");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchGoods()]);

    return result;
};

// Fiscal
const getFiscalReport = async () => {
    const fetchFiscalReport = async () => {
        const response = await fetch(SERVER_HOST + "?action=get_fiscal_report");

        const data = await response.json();

        if (data.status !== "success") {
            throw new Error("Failed to fetch data!");
        }

        return data.data;
    };

    const [result] = await Promise.all([fetchFiscalReport()]);

    return result;
};

export {
    getSalesReport,
    getOverallIncome,
    getAverageCheck,
    getCheckCount,
    getPaymentTypes,
    getPeakHours,
    getStaffEfficiency,
    getDepartmentEfficiency,
    getPopularMenu,
    getSuppliersDebt,
    getCashBalances,
    getStopList,
    getLossSoldGoods,
    getCashAccounts,
    getArticles,
    getDepartments,
    getCounterparties,
    getDiscounts,
    getFolders,
    getGoods,
    getDebtReport,
    getCashflowReport,
    getFiscalReport,
};
